import { HotelForm as Form } from 'hotel/HotelForm';
import { useNavigate } from 'hotel/navigation';
import { times } from 'lodash';
import React, { useMemo } from 'react';

import { SecondaryButton } from 'components/Button';
import Card from 'components/Card';
import ClientTeamMemberSelect from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import { CreateAmadeusBookingMutation } from 'generated/graphql';
import { useAppSelector } from 'store/store';

interface Props {
  booking: CreateAmadeusBookingMutation['createAmadeusBooking'];
  filterToCurrentUser?: boolean;
}

const GuestCard: React.FC<Props> = ({
  booking,
  filterToCurrentUser = false,
}) => {
  const navigate = useNavigate();
  const clientTeamMemberId = useAppSelector(
    (state) => state.user.profile?.clientTeamMemberId,
  );

  // Filter rooms to only those containing the current user if filtering is enabled
  const filteredRooms = useMemo(() => {
    if (!filterToCurrentUser || !clientTeamMemberId) {
      return booking.bookingRooms;
    }
    return booking.bookingRooms.filter((room) =>
      room.guests?.some(
        (guest) => String(guest.id) === String(clientTeamMemberId),
      ),
    );
  }, [booking.bookingRooms, filterToCurrentUser, clientTeamMemberId]);

  // Derive room info from filtered rooms
  const totalRooms = filteredRooms.length;
  const maxGuestsInAnyRoom = Math.max(
    ...filteredRooms.map((room) => room.guests?.length || 0),
    1, // At least 1 guest slot per room
  );

  // Memoize initial values creation to avoid unnecessary recalculations
  const initialValues = useMemo(
    () => ({
      guests: times(totalRooms, (roomIndex) => {
        const bookingRoom = filteredRooms[roomIndex];
        const roomGuests = bookingRoom?.guests || [];

        return times(maxGuestsInAnyRoom, (guestIndex) => {
          const guest = roomGuests[guestIndex];

          if (guest) {
            // Transform guest data to match ClientTeamMemberSelect expected format
            return {
              id: guest.id,
              firstName: guest.firstName,
              lastName: guest.lastName,
              // Add other fields if available in the future
            };
          }
          // Return undefined for empty slots (implicit)
        });
      }),
    }),
    [filteredRooms, totalRooms, maxGuestsInAnyRoom],
  );

  const handleGuestUpdate = () => {
    // Handle guest update submission
    // Guest updates handled
    // TODO: Implement guest update mutation
  };

  return (
    <Card className="h-auto overflow-visible">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-bold">
          {filterToCurrentUser ? 'Your Room' : 'Guests'}
        </h2>
        {!filterToCurrentUser && (
          <SecondaryButton
            text="Live Roster View"
            onClick={() => navigate('/roster')}
            className="h-8"
          />
        )}
      </div>

      <Form
        onSubmit={handleGuestUpdate}
        initialValues={initialValues}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              {filteredRooms.map((bookingRoom, roomIndex) => {
                const currentGuestCount = bookingRoom?.guests?.length || 0;

                return (
                  <div
                    key={bookingRoom.id}
                    className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4 hover:bg-primaryGray/10"
                  >
                    <div className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">
                        {filterToCurrentUser
                          ? 'Your Room'
                          : `Room ${roomIndex + 1}`}{' '}
                        ({currentGuestCount} guest
                        {currentGuestCount !== 1 ? 's' : ''})
                      </span>
                    </div>

                    {times(maxGuestsInAnyRoom, (guestIndex) => (
                      <ClientTeamMemberSelect
                        key={guestIndex}
                        name={`guests[${roomIndex}][${guestIndex}]`}
                      />
                    ))}
                  </div>
                );
              })}
            </form>
          );
        }}
      />
    </Card>
  );
};

export default GuestCard;
